import { useEffect } from 'react';
import { PitchHero } from '@/components/pitch/PitchHero';
import { PitchProgress } from '@/components/pitch/PitchProgress';
import { WhyNow } from '@/components/pitch/WhyNow';
import { TheProblem } from '@/components/pitch/TheProblem';
import { TheProduct } from '@/components/pitch/TheProduct';
import { RoiSummary } from '@/components/pitch/RoiSummary';
import { MarketSummary } from '@/components/pitch/MarketSummary';
import { Roadmap } from '@/components/pitch/Roadmap';
import { BusinessModel } from '@/components/pitch/BusinessModel';
import { SecurityCompliance } from '@/components/pitch/SecurityCompliance';
import { GoToMarket } from '@/components/pitch/GoToMarket';
import { UseOfFunds } from '@/components/pitch/UseOfFunds';
import { PitchFooter } from '@/components/pitch/PitchFooter';
import { PITCH_SECTIONS } from '@/components/pitch/sections';

/**
 * Investor run-through.
 *
 * Single long-scroll narrative at /pitch. All pitch-only UI lives in
 * `src/components/pitch/` so the whole investor surface can be ripped
 * out without touching the prototype. Links into /roi for the per-firm
 * ROI / UK-market / international deep-dives from the deck summaries rather
 * than duplicating the full calculators and supporting datasets here.
 *
 * Public route (no auth). Hidden from in-app nav.
 */
/**
 * The gradient used to resolve at #1E293B — Tailwind slate. That hex is
 * only ~33% saturated against ~47% for the navy above it, so the long
 * empty stretch above the footer drifted grey and dusty while the top of
 * the page stayed a deep blue.
 *
 * Every stop now holds saturation in the low fifties, so the page reads
 * as one navy throughout. It lifts gently through the closing sections
 * and settles back down into the footer rather than ending on its
 * lightest, flattest note.
 */
const PAGE_BG =
  'linear-gradient(180deg, #0B1120 0%, #0F172A 35%, #121E3A 72%, #0E1830 100%)';

const PITCH_SECTIONS_WHY_NOW = PITCH_SECTIONS.map((section) =>
  section.id === 'signal' ? { ...section, label: 'Why now' } : section,
);

export function PitchPage() {
  useEffect(() => {
    const previous = document.title;
    document.title = 'Asset Hound · Investor run through';
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <div
      className="relative min-h-screen overflow-x-hidden text-ink"
      style={{ background: PAGE_BG }}
    >
      <PageAtmosphere />
      <PitchProgress sections={PITCH_SECTIONS_WHY_NOW} />

      <main className="relative z-10">
        <PitchHero investorCaseFlow />
        <WhyNow />
        <TheProblem />
        <TheProduct />
        <RoiSummary linkToCalculator />
        <MarketSummary />
        <Roadmap />
        <BusinessModel />
        <SecurityCompliance />
        <GoToMarket />
        <UseOfFunds />
      </main>

      <PitchFooter />
    </div>
  );
}

/**
 * Fixed atmosphere behind the whole run-through.
 *
 * The page was a single 180° linear-gradient, which on a projector or an
 * OLED panel bands into visible steps and reads as flat vinyl. Three cheap
 * layers fix that: a wide brand wash that keeps the upper third alive, a
 * vignette that pulls the eye to the content column, and ~3% film grain
 * that dithers the gradient so it never posterises.
 *
 * All three are fixed and `pointer-events-none`, so they cost one paint
 * and never participate in scrolling or hit-testing.
 */
function PageAtmosphere() {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0">
      <div
        className="absolute left-1/2 top-[-20vh] h-[70vh] w-[140vw] -translate-x-1/2 rounded-full opacity-[0.10] blur-[160px]"
        style={{ background: '#3B82F6' }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 80% at 50% 40%, transparent 42%, rgba(3, 7, 18, 0.55) 100%)',
        }}
      />
      <div className="pitch-grain absolute inset-0 opacity-[0.032] mix-blend-overlay" />
    </div>
  );
}
